import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle,
  ChevronRight,
  Printer,
  AlertCircle,
  FileText,
  Download,
  ZoomIn,
  RefreshCw,
} from "lucide-react";
import PrintCertificatePreview from "../../common/PrintCertificatePreview/PrintCertificatePreview";
import PrintRequestModal from "../../common/PrintRequestModal/PrintRequestModal";
import { GET, POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import EntityConstants from "../../configs/EntityConstants";
import DownloadRequestModal from "../../common/DownloadRequestModal/DownloadRequestModal";
import useNavigationStore from "../../store/navigationStore";

const PRINT_REQUESTS_POLLING_INTERVAL = 3000;

export default function PrintPdf() {
  const { legacyTransactionId: paramLegacyTransactionId } = useParams();
  const navigate = useNavigate();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const legacyTransactionId =
    paramLegacyTransactionId || localStorage.getItem("legacyTransactionId");

  const credentialTemplateId = localStorage.getItem("credentialTemplateId");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedRows] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [printQuality] = useState("high");
  const [paperSize] = useState("a4");
  const [showPrintRequestModal, setShowPrintRequestModal] = useState(false);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [documentData, setDocumentData] = useState({
    total_recipients: 0,
    certificate_preview: "",
  });
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [printRequestsData, setPrintRequestsData] = useState({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });
  const [offset, setOffset] = useState(0);
  const [showDownloadRequestModal, setShowDownloadRequestModal] =
    useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [isPollingActive, setIsPollingActive] = useState(false);
  const limit = 5;

  const pdfPreviewCanvasRef = useRef(null);
  const pdfPrintPreviewCanvasRef = useRef(null);
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  useEffect(() => {
    if (!currentProgramme || !currentBatch || !legacyTransactionId) {
      console.warn("Missing essential context. Redirecting to workspace.");
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, legacyTransactionId, navigate]);

  const renderPdfPreview = async (url, canvasRef, scale = 0.3) => {
    if (!url || !canvasRef.current) {
      console.log("Skipping PDF render - no URL or canvas");
      return null;
    }

    try {
      const loadingTask = pdfjsLib.getDocument({
        url,
        withCredentials: false,
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.style.height = `${viewport.height}px`;
      canvas.style.width = `${viewport.width}px`;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      return canvas.toDataURL();
    } catch (error) {
      console.error("Error rendering PDF:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchDocumentData = async () => {
      if (!legacyTransactionId) {
        return;
      }
      try {
        const response = await GET(
          `${EntityConstants.GET_USER_DETAILS_PRINT_CERTIFICATES}?issuer_credential_template_id=${legacyTransactionId}`
        );
        const data = await response.json();
        setDocumentData(data);
        if (data.certificate_preview) {
          setIsPdfLoading(true);
          try {
            const dataUrl = await renderPdfPreview(
              data.certificate_preview,
              pdfPreviewCanvasRef
            );
            setPreviewImageUrl(dataUrl);
            await renderPdfPreview(
              data.certificate_preview,
              pdfPrintPreviewCanvasRef
            );
          } finally {
            setIsPdfLoading(false);
          }
        }
      } catch (error) {
        console.error("Error fetching document data:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch document data",
        });
      }
    };

    fetchDocumentData();
  }, [legacyTransactionId]);

  const handleCreatePrintRequest = async () => {
    setIsTableLoading(true);
    try {
      const response = await POST(
        `${EntityConstants.POST_REQUEST_CERTIFICATE}`,
        {
          issuer_credential_template_id: legacyTransactionId,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create print request");
      }
      Toast({
        type: "success",
        title: "Success",
        message: "Print request created successfully",
      });
      setShowDownloadRequestModal(true);
      setShowPrintRequestModal(false);

      const newData = await fetchPrintRequests(
        activeTab === "all" ? "" : activeTab,
        searchTerm,
        offset,
        false
      );

      startPollingIfNeeded(newData);
    } catch (error) {
      console.error("Error creating print request:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to create print request",
      });
    } finally {
      setIsTableLoading(false);
    }
  };

  const fetchPrintRequests = async (
    section = "",
    search = "",
    offset = 0,
    showLoading = true
  ) => {
    if (!legacyTransactionId) {
      showLoading && setIsTableLoading(false);
      return;
    }
    showLoading && setIsTableLoading(true);
    try {
      let url = `${EntityConstants.GET_USER_PDF_DETAILS}?issuer_credential_template_id=${legacyTransactionId}&limit=${limit}&offset=${offset}`;
      if (section) url += `&status=${section}`;
      if (search) url += `&search=${search}`;

      const response = await GET(url);
      const data = await response.json();
      setPrintRequestsData(data);
      return data;
    } catch (error) {
      console.error("Error fetching print requests:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch print requests",
      });
      throw error;
    } finally {
      showLoading && setIsTableLoading(false);
    }
  };

  const needsPolling = (results) => {
    if (!results || results.length === 0) return false;
    return results.some(
      (request) =>
        request.status === "Started" || request.status === "In-Progress"
    );
  };

  const startPollingIfNeeded = (data) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
      setIsPollingActive(false);
    }

    if (needsPolling(data.results)) {
      setIsPollingActive(true);
      const interval = setInterval(async () => {
        try {
          const newData = await fetchPrintRequests(
            activeTab === "all" ? "" : activeTab,
            searchTerm,
            offset,
            false
          );
          const shouldStopPolling = !needsPolling(newData.results);

          if (shouldStopPolling) {
            clearInterval(interval);
            setPollingInterval(null);
            setIsPollingActive(false);

            const hasCompletedRequests = newData.results.some(
              (request) => request.status === "Completed"
            );

            if (hasCompletedRequests) {
              Toast({
                type: "success",
                title: "Print Request Completed",
                message: "Your print request has been processed successfully.",
                persistent: true,
              });
            }
          }
        } catch (error) {
          console.error("Polling error:", error);
          clearInterval(interval);
          setPollingInterval(null);
          setIsPollingActive(false);
        }
      }, PRINT_REQUESTS_POLLING_INTERVAL);

      setPollingInterval(interval);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchPrintRequests(
        activeTab === "all" ? "" : activeTab,
        searchTerm,
        offset,
        true
      );
      startPollingIfNeeded(data);
    };

    fetchData();
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [activeTab, searchTerm, offset, legacyTransactionId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchPrintRequests(activeTab === "all" ? "" : activeTab, searchTerm, 0);
  };

  const handleNext = () => {
    if (printRequestsData.next) {
      setOffset(offset + limit);
    }
  };

  const handlePrevious = () => {
    if (printRequestsData.previous) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleConfirmPrint = () => {
    setShowPrintRequestModal(false);
    setShowConfirmModal(false);
    alert(
      "Print request accepted! You will receive an email when the files are ready for download."
    );
  };

  const handleNewPrintRequest = () => {
    setShowPrintRequestModal(true);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace`);
  };

  const handleTemplatesClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const handlePendingIssuanceClick = () => {
    console.log("The legacy transaction id is", legacyTransactionId);
    navigate(
      `/issuer/workspace/batches/summary/${credentialTemplateId}/${legacyTransactionId}?templateType=Legacy`
    );
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback(e);
    }
  };

  const handlePreviewClick = () => {
    if (documentData.certificate_preview) {
      setIsPreviewModalOpen(true);
    }
  };

  const recentFiles = printRequestsData.results.slice(0, 4).map((file) => ({
    name: file.file_name,
    url: file.pdf_download,
    date: file.requested_at,
  }));

  return (
    <div className="min-h-screen mt-20 bg-cfSecondary w-full">
      {/* Breadcrumbs */}
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full`}
        >
          <div className="">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span
                    onClick={handleProgrammeClick}
                    onKeyDown={(e) => handleKeyDown(e, handleProgrammeClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer"
                  >
                    {currentProgramme.programme_name || "Programme"}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span
                    onClick={handleBatchClick}
                    onKeyDown={(e) => handleKeyDown(e, handleBatchClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer"
                  >
                    {currentBatch.batch_name || "Batch"}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span
                    onClick={handleTemplatesClick}
                    onKeyDown={(e) => handleKeyDown(e, handleTemplatesClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer"
                  >
                    Templates
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span
                    onClick={handlePendingIssuanceClick}
                    onKeyDown={(e) => handleKeyDown(e, handlePendingIssuanceClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer"
                  >
                    Summary
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-800 font-medium">PDF Print</span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between my-4 px-4">
        <div className="flex gap-2 justify-start">
          <div
            onClick={handlePendingIssuanceClick}
            onKeyDown={(e) => handleKeyDown(e, handlePendingIssuanceClick)}
            role="button"
            tabIndex={0}
            className="mt-[0.2rem] cursor-pointer"
          >
            <img src={ArrowLeftIcon} alt="" className="" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Print Certificates
            </h1>
            <p className="text-gray-500">
              Select recipients and download certificates for printing
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleNewPrintRequest}
            className="flex items-center px-3 py-2 border border-transparent rounded-[8px] shadow-sm text-md font-medium text-white bg-[#3538CD] hover:bg-indigo-700"
          >
            <Printer className="mr-2 h-4 w-4" />
            New Print Request
          </button>
        </div>
      </div>

      {/* Stats Cards and Preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 px-4">
        {/* Total Recipients Widget */}
        <div className="bg-white border rounded-xl p-4 shadow-sm flex flex-col items-center justify-center">
          <div className="text-base text-gray-500 mb-2">Total Recipients</div>
          <div className="text-3xl font-bold text-center">
            {documentData.total_recipients ?? <CfLoader size="small" />}
          </div>
        </div>

        {/* Recent Files Widget */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="text-sm text-gray-500 mb-2">Recent Files</div>
          <div className="space-y-2 overflow-y-auto">
            {recentFiles.length > 0 ? (
              recentFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 p-2 rounded-md"
                >
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-indigo-600 mr-2" />
                    <span className="text-sm truncate max-w-[250px]">
                      {file.name ? file.name : "Unavailable"}
                    </span>
                  </div>
                  {file.url ? (
                    <a
                      href={file.url}
                      download
                      className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  ) : (
                    <button
                      className="h-8 w-8 p-0 text-gray-300 cursor-not-allowed flex items-center justify-center"
                      disabled
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-md text-gray-600 font-semibold pt-[5rem] text-center">
                No recent files found
              </div>
            )}
          </div>
        </div>

        {/* Document Preview */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="text-sm text-gray-500 mb-2">Document Preview</div>
          {isPdfLoading ? (
            <div className="aspect-[1.414] bg-gray-50 rounded-md overflow-hidden flex items-center justify-center w-3/4 mx-auto">
              <CfLoader size="small" />
            </div>
          ) : (
            <div
              className="aspect-[1.414] bg-gray-50 rounded-md overflow-hidden flex items-center justify-center w-3/4 mx-auto relative cursor-pointer"
              onMouseEnter={() => setIsPreviewHovered(true)}
              onMouseLeave={() => setIsPreviewHovered(false)}
              onClick={handlePreviewClick}
              onKeyDown={(e) => handleKeyDown(e, handlePreviewClick)}
              role="button"
              tabIndex={0}
            >
              {previewImageUrl ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={previewImageUrl}
                    alt="Certificate preview"
                    className="max-h-full max-w-full object-contain"
                  />
                  {isPreviewHovered && (
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <div className="bg-white bg-opacity-80 rounded-full p-3">
                        <ZoomIn className="h-6 w-6 text-gray-700" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-400">No preview available</div>
              )}
            </div>
          )}
        </div>
        <canvas ref={pdfPreviewCanvasRef} style={{ display: "none" }} />
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4 px-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Print Requests</h2>
          <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
            <div className="flex">
              <button
                onClick={() => {
                  setActiveTab("all");
                  setOffset(0);
                }}
                className={`px-4 py-2 text-sm font-medium focus:outline-none ${
                  activeTab === "all"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                All Requests
              </button>
              <button
                onClick={() => {
                  setActiveTab("completed");
                  setOffset(0);
                }}
                className={`px-4 py-2 text-sm font-medium focus:outline-none ${
                  activeTab === "completed"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Done
              </button>
              <button
                onClick={() => {
                  setActiveTab("progress");
                  setOffset(0);
                }}
                className={`px-4 py-2 text-sm font-medium focus:outline-none ${
                  activeTab === "progress"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Pending
              </button>
            </div>
            <button
              onClick={() => {
                fetchPrintRequests(
                  activeTab === "all" ? "" : activeTab,
                  searchTerm,
                  offset,
                  true
                );
              }}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border-l border-gray-300 flex items-center justify-center"
              title="Refresh table"
            >
              <RefreshCw
                className={`h-4 w-4 ${isPollingActive ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto">
          <form
            onSubmit={handleSearch}
            className={`bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] md:ml-[0.630rem] self-center ml-[0rem] md:self-end md:m-0 border border-gray-400`}
          >
            <input
              type="text"
              name="search"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Requests..."
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      {/* Print Requests Table */}
      <div className="rounded-lg overflow-hidden pb-4 px-4">
        {isTableLoading ? (
          <div className="flex justify-center items-center h-64">
            <CfLoader />
          </div>
        ) : printRequestsData.results.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-[#E0EAFF] h-[3.563rem] text-left">
                <tr>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                    onClick={() => handleSort("requestedBy")}
                    onKeyDown={(e) => handleKeyDown(e, () => handleSort("requestedBy"))}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">Requested By</div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                    onClick={() => handleSort("requestedAt")}
                    onKeyDown={(e) => handleKeyDown(e, () => handleSort("requestedAt"))}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">Requested At</div>
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("file_name")}
                    onKeyDown={(e) => handleKeyDown(e, () => handleSort("file_name"))}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">File name</div>
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File Details
                  </th>
                  <th
                    className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("status")}
                    onKeyDown={(e) => handleKeyDown(e, () => handleSort("status"))}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center">Status</div>
                  </th>
                  <th className="items-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {printRequestsData.results.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {request.requested_by
                        ? request.requested_by
                        : "Unavailable"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(request.requested_at).toLocaleString(
                        undefined,
                        {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        }
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {request.file_name ? request.file_name : "Unavailable"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col">
                        <span>
                          {request.file_details[0]?.recipient_count || 0}{" "}
                          recipients
                        </span>
                        <span>
                          {request.file_details[0]?.file_size || "0 MB"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {request.status === "Completed" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" /> Done
                        </span>
                      ) : request.status === "Failed" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle className="h-3 w-3 mr-1" /> Failed
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <AlertCircle className="h-3 w-3 mr-1" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="pl-10 pr-6 py-4 whitespace-nowrap text-sm">
                      <a
                        href={request.pdf_download}
                        download
                        className={`h-8 w-8 p-0 ${
                          request.status !== "Completed" ||
                          request.file_details[0]?.file_size === "0.0 MB"
                            ? "text-gray-300 cursor-not-allowed pointer-events-none"
                            : "text-indigo-600 hover:text-indigo-800"
                        } flex items-center justify-center`}
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex justify-center items-center h-64 bg-white rounded-lg">
            <div className="text-gray-700 font-semibold text-lg">
              No results found
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 w-full">
          <button
            onClick={handlePrevious}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={!printRequestsData.previous}
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                />
              </svg>
              <span>previous</span>
            </span>
          </button>

          <button
            onClick={handleNext}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={!printRequestsData.next}
          >
            <span className="flex items-center justify-center gap-2">
              <span>Next</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Confirm Print Request</h3>
              <p className="text-sm text-gray-500">
                You are about to request high-resolution print files for{" "}
                {selectedRows.length} certificates. These will be prepared and
                sent to your email.
              </p>
            </div>

            <div className="p-4 space-y-6">
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <AlertCircle className="h-5 w-5" />
                <p className="font-medium">Print Request Details</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Print Quality</p>
                    <p className="font-medium">
                      {printQuality === "draft"
                        ? "Draft (72 DPI)"
                        : printQuality === "standard"
                        ? "Standard (150 DPI)"
                        : printQuality === "high"
                        ? "High (300 DPI)"
                        : "Ultra High (600 DPI)"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Paper Size</p>
                    <p className="font-medium">
                      {paperSize === "a4"
                        ? "A4 (210 × 297 mm)"
                        : paperSize === "letter"
                        ? "Letter (8.5 × 11 in)"
                        : paperSize === "legal"
                        ? "Legal (8.5 × 14 in)"
                        : "A3 (297 × 420 mm)"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      Number of Certificates
                    </p>
                    <p className="font-medium">{selectedRows.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Estimated File Size</p>
                    <p className="font-medium">
                      {printQuality === "draft"
                        ? "~2 MB"
                        : printQuality === "standard"
                        ? "~8 MB"
                        : printQuality === "high"
                        ? "~25 MB"
                        : "~80 MB"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Note:</span> High-resolution
                  files may take some time to prepare. You will receive an email
                  with a download link when your files are ready.
                </p>
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPrint}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Confirm Print Request
              </button>
            </div>
          </div>
        </div>
      )}
      {showPrintRequestModal && (
        <PrintRequestModal
          isOpen={showPrintRequestModal}
          onClose={() => setShowPrintRequestModal(false)}
          totalRecipients={documentData.total_recipients}
          pdfUrl={documentData.certificate_preview}
          isLoading={isTableLoading}
          onCreatePrintRequest={handleCreatePrintRequest}
        />
      )}
      <PrintCertificatePreview
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        UploadedPdfGif={documentData.certificate_preview}
        header={"Certificate Preview"}
        isPdf={true}
      />
      <DownloadRequestModal
        isOpen={showDownloadRequestModal}
        isSuccess={true}
        message="Your request has been initiated. You will be notified when the files are ready for download."
        header="Success"
        onClose={() => setShowDownloadRequestModal(false)}
      />
    </div>
  );
}